import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import {
    faArrowsRotate,
    faAtomSimple,
    faBell,
    faBooks,
    faBoxes,
    faBuildingColumns,
    faDatabase,
    faChartPieSimpleCircleDollar,
    faCircleNodes,
    faCloudDownload,
    faCoins,
    faCreditCard,
    faFlag,
    faFlaskGear,
    faGlobe,
    faHome,
    faLock,
    faMessageDollar,
    faMobile,
    faMoneyBills,
    faMoneyBillTransfer,
    faOilWell,
    faShapes,
    faSquareList,
    faTag,
    faUser,
    faUserGroup,
    faLeaf,
    faRocket,
    faPiggyBank,
    faMoneyBillWave,
    faNewspaper,
    faLandmarkFlag,
    faBriefcase,
    faCrosshairs,
    faSquarePollHorizontal,
    faPlug,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconTypes, LucideIconTypes } from "./types";
import {
    faMegaphone,
    faSeedling,
    faStar,
    faRectangleTerminal,
    faChartLine,
    faMagnifyingGlass,
    faBolt,
    faChartMixedUpCircleDollar,
    faQuoteLeft,
} from "@fortawesome/pro-solid-svg-icons";
import {
    ArrowLeftRightIcon,
    Building2Icon,
    CalculatorIcon,
    CoinsIcon,
    DollarSignIcon,
    EyeIcon,
    FileIcon,
    FileTextIcon,
    GaugeIcon,
    GavelIcon,
    GitBranchIcon,
    InfoIcon,
    LandmarkIcon,
    LayersIcon,
    Link2Icon,
    type LucideIcon,
    RepeatIcon,
    ScaleIcon,
    ShieldIcon,
    StoreIcon,
    TrendingUpIcon,
    UsersIcon,
} from "lucide-react";

// // // //

const ICON_TO_FONT_AWESOME: { [key in IconTypes]: IconDefinition } = {
    [IconTypes.ArrowsRotate]: faArrowsRotate,
    [IconTypes.AtomSimple]: faAtomSimple,
    [IconTypes.Bell]: faBell,
    [IconTypes.Books]: faBooks,
    [IconTypes.Boxes]: faBoxes,
    [IconTypes.BriefcaseBusiness]: faBriefcase,
    [IconTypes.Crosshairs]: faCrosshairs,
    [IconTypes.Database]: faDatabase,
    [IconTypes.BuildingColumns]: faBuildingColumns,
    [IconTypes.Chart]: faChartLine,
    [IconTypes.ChartCircleDollar]: faChartMixedUpCircleDollar,
    [IconTypes.CircleNodes]: faCircleNodes,
    [IconTypes.CloudDownload]: faCloudDownload,
    [IconTypes.Code]: faRectangleTerminal,
    [IconTypes.Coins]: faCoins,
    [IconTypes.CreditCard]: faCreditCard,
    [IconTypes.Flag]: faFlag,
    [IconTypes.FlaskGear]: faFlaskGear,
    [IconTypes.Globe]: faGlobe,
    [IconTypes.Plug]: faPlug,
    [IconTypes.Home]: faHome,
    [IconTypes.LandmarkFlag]: faLandmarkFlag,
    [IconTypes.Leaf]: faLeaf,
    [IconTypes.Lightning]: faBolt,
    [IconTypes.Lock]: faLock,
    [IconTypes.Invest]: faSeedling,
    [IconTypes.Megaphone]: faMegaphone,
    [IconTypes.MessageDollar]: faMessageDollar,
    [IconTypes.Mobile]: faMobile,
    [IconTypes.MoneyBill]: faMoneyBillWave,
    [IconTypes.MoneyBills]: faMoneyBills,
    [IconTypes.MoneyBillTransfer]: faMoneyBillTransfer,
    [IconTypes.Newspaper]: faNewspaper,
    [IconTypes.Rocket]: faRocket,
    [IconTypes.OilWell]: faOilWell,
    [IconTypes.PieChartCircleDollar]: faChartPieSimpleCircleDollar,
    [IconTypes.PiggyBank]: faPiggyBank,
    [IconTypes.QuoteLeft]: faQuoteLeft,
    [IconTypes.Shapes]: faShapes,
    [IconTypes.Search]: faMagnifyingGlass,
    [IconTypes.SquareList]: faSquareList,
    [IconTypes.SquarePollHorizontal]: faSquarePollHorizontal,
    [IconTypes.Star]: faStar,
    [IconTypes.Tag]: faTag,
    [IconTypes.User]: faUser,
    [IconTypes.UserGroup]: faUserGroup,
};

const ICON_TO_LUCIDE: { [key in LucideIconTypes]: LucideIcon } = {
    [LucideIconTypes.arrow_left_right]: ArrowLeftRightIcon,
    [LucideIconTypes.building]: Building2Icon,
    [LucideIconTypes.calculator]: CalculatorIcon,
    [LucideIconTypes.coins]: CoinsIcon,
    [LucideIconTypes.dollar_sign]: DollarSignIcon,
    [LucideIconTypes.eye]: EyeIcon,
    [LucideIconTypes.gavel]: GavelIcon,
    [LucideIconTypes.info]: InfoIcon,
    [LucideIconTypes.landmark]: LandmarkIcon,
    [LucideIconTypes.layers]: LayersIcon,
    [LucideIconTypes.link]: Link2Icon,
    [LucideIconTypes.scale]: ScaleIcon,
    [LucideIconTypes.shield]: ShieldIcon,
    [LucideIconTypes.store]: StoreIcon,
    [LucideIconTypes.trending_up]: TrendingUpIcon,
    [LucideIconTypes.users]: UsersIcon,
    [LucideIconTypes.git_branch]: GitBranchIcon,
    [LucideIconTypes.gauge]: GaugeIcon,
    [LucideIconTypes.repeat]: RepeatIcon,
    [LucideIconTypes.file]: FileIcon,
    [LucideIconTypes.file_text]: FileTextIcon,
};

export function isValidIconType(value: string): boolean {
    return [
        ...Object.values(IconTypes),
        ...Object.values(LucideIconTypes),
    ].includes(value as IconTypes | LucideIconTypes);
}

/**
 * Renders an icon for an individual search result
 * Supports icon rendering via Image URL or by using a predetermined FontAwesome or Lucide icon
 */
export function Icon(props: {
    icon: string | IconTypes | LucideIconTypes;
    className?: string;
}) {
    const { icon, className } = props;

    // Return null if icon isn't valid
    if (!isValidIconType(icon)) {
        return null;
    }

    if (Object.values(LucideIconTypes).includes(icon as LucideIconTypes)) {
        const LucideIconComponent = ICON_TO_LUCIDE[icon as LucideIconTypes];
        return <LucideIconComponent className={className} />;
    }

    return (
        <FontAwesomeIcon
            className={className}
            icon={ICON_TO_FONT_AWESOME[icon as IconTypes]}
        />
    );
}
